import { fetchClient } from "@/api/api";
import type {
	BatchDeleteConsoleProfilesInput,
	BatchDeleteConsoleProfilesOutput,
	ConsoleProfilesInput,
	ConsoleProfilesOutput,
	CreateConsoleProfileInput,
	CreateConsoleProfileOutput,
	DeleteConsoleProfileInput,
	DeleteConsoleProfileOutput,
	UpdateConsoleProfileInput,
	UpdateConsoleProfileOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import type { ApiError } from "@/utils/fetching";
import type { CustomMutationHook, CustomQueryHook } from "@/utils/react-query";
import { invalidateQueries } from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PLAYERS_AUCTION_KEYS } from "../players_auction";
import { CONSOLE_SERVICE_KEYS } from "./keys";

export const useGetAllConsoles: CustomQueryHook<
	ConsoleProfilesOutput,
	ConsoleProfilesInput
> = () => {
	return useQuery<ConsoleProfilesOutput, ApiError>({
		queryKey: CONSOLE_SERVICE_KEYS.useGetAllConsoles(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/console/", {
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
	});
};

export const useCreateConsole: CustomMutationHook<
	CreateConsoleProfileInput,
	CreateConsoleProfileOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		CreateConsoleProfileOutput,
		ApiError,
		CreateConsoleProfileInput
	>({
		mutationKey: CONSOLE_SERVICE_KEYS.useCreateConsole(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/console/", {
				body,
				throwOnError: true,
			});
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(CONSOLE_SERVICE_KEYS.useGetAllConsoles());
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayersAuctionInit());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useUpdateConsole = () => {
	const { toastApiError } = useApiError();

	return useMutation<
		UpdateConsoleProfileOutput,
		ApiError,
		UpdateConsoleProfileInput
	>({
		mutationKey: CONSOLE_SERVICE_KEYS.useUpdateConsole(),
		mutationFn: async ({ console_id, ...body }) => {
			const { data, error } = await fetchClient.PATCH(
				"/v1/fc/console/{console_id}",
				{
					params: { path: { console_id } },
					body,
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(CONSOLE_SERVICE_KEYS.useGetAllConsoles());
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayersAuctionInit());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useDeleteConsole: CustomMutationHook<
	DeleteConsoleProfileInput,
	DeleteConsoleProfileOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: CONSOLE_SERVICE_KEYS.useDeleteConsole(),
		mutationFn: async (payload) => {
			const { data, error } = await fetchClient.DELETE(
				"/v1/fc/console/{console_id}",
				{
					params: {
						path: { console_id: payload.console_id },
					},
					throwOnError: true,
				},
			);
			if (error) {
				throw error;
			}
			return data;
		},
		onSuccess: () => {
			invalidateQueries(CONSOLE_SERVICE_KEYS.useGetAllConsoles());
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayersAuctionInit());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useBatchDeleteConsole: CustomMutationHook<
	BatchDeleteConsoleProfilesInput,
	BatchDeleteConsoleProfilesOutput
> = () => {
	const { toastApiError } = useApiError();

	return useMutation({
		mutationKey: CONSOLE_SERVICE_KEYS.useBatchDeleteConsoles(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/fc/console/delete", {
				body,
				throwOnError: true,
			});

			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			invalidateQueries(CONSOLE_SERVICE_KEYS.useGetAllConsoles());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};
